import React from 'react'
import { Badge } from 'reactstrap'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import Select from 'react-select'
import { withTranslation } from 'react-i18next'

import AlertMessage from '../AlertMessage'
import integrationsActions from '../../integrations/actions'
import PageLoader from '../PageLoader'

class GitOptionImportBranch extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      platformBranches: []
    }
  }

  componentDidMount() {
    this.refereshBranches()
  }

  componentDidUpdate(prevProps) {
    const {
      branchesFetchingStatus: prevBranchesFetchingStatus,
      repositoryId: prevRepositoryId
    } = prevProps
    const {
      branchesFetchingStatus,
      repositoryId
    } = this.props

    if (prevRepositoryId !== repositoryId) this.refereshBranches()

    if (prevBranchesFetchingStatus === 'fetching'
        && branchesFetchingStatus === 'succeeded'
    ) {
      this.updatePlatformBranches()
    }
  }

  refereshBranches() {
    const { dispatch, repositoryId } = this.props

    if (repositoryId) {
      dispatch(integrationsActions.fetchBranches(repositoryId, 'gitlab'))
    }
  }

  updatePlatformBranches() {
    const { branches } = this.props

    this.setState({
      platformBranches: [].concat(branches?.gitlab).sort(
        (a, b) => a.label.localeCompare(b.label)
      )
    })
  }

  render() {
    const {
      branchesFetchingError,
      branchesFetchingStatus,
      onChange,
      presentation,
      repositoryId,
      selectedBranch,
      selectedBranchName,
      t
    } = this.props

    const { platformBranches } = this.state

    const i18nBase = 'GitOptions.options.branch'

    const renderBranchOption = ({ label, default: isDefault, protected: isProtected }) => (
      <div className="d-flex flex-row">
        {label}
        {isDefault && (
          <div className="d-flex flex-column justify-content-center ms-2">
            <Badge color="primary">
              {t(`${i18nBase}.badges.default`)}
            </Badge>
          </div>
        )}
        {isProtected && (
          <div className="d-flex flex-column justify-content-center ms-2">
            <Badge color="success">
              {t(`${i18nBase}.badges.protected`)}
            </Badge>
          </div>
        )}
      </div>
    )

    const defaultBranch = platformBranches?.find((item) => item.default)
    let preSelectedBranch

    if (selectedBranch === null && selectedBranchName !== null) {
      preSelectedBranch = platformBranches?.find((item) => item.label === selectedBranchName)
    }

    const selectControl = (
      <Select
        formatOptionLabel={(option) => renderBranchOption(option)}
        id="git-branch"
        name="gitBranch"
        onChange={(selected) => onChange(selected)}
        options={platformBranches}
        styles={{
          control: (css) => ({
            ...css,
            width: presentation === 'stacked' ? '100%' : 300
          })
        }}
        value={preSelectedBranch || selectedBranch || defaultBranch}
      />
    )

    if (presentation === 'stacked') {
      return (
        <div className="d-flex flex-column mb-2">
          {['idle', 'fetching'].includes(branchesFetchingStatus) && (
            <PageLoader message={t(`${i18nBase}.fetching`)} />
          )}

          {branchesFetchingStatus === 'failed' && (
            <AlertMessage>
              {typeof branchesFetchingError === 'string'
                ? branchesFetchingError
                : t('errors.somethingWentWrong', { ns: 'commons' })}
            </AlertMessage>
          )}

          {branchesFetchingStatus === 'succeeded' && repositoryId
            && (platformBranches === null || platformBranches.length === 0)
            && (
              <AlertMessage>
                {t(`${i18nBase}.errors.noBranchesFound`)}
              </AlertMessage>
            )}

          {branchesFetchingStatus === 'succeeded' && repositoryId
            && platformBranches && platformBranches.length > 0
            && selectControl}
        </div>
      )
    }

    return (
      <div className="d-flex flex-row justify-content-between mb-2">
        <div className="d-flex flex-column justify-content-center">
          {t(`${i18nBase}.heading`)}
        </div>

        {['idle', 'fetching'].includes(branchesFetchingStatus) && (
          <PageLoader
            message={t(`${i18nBase}.fetching`)}
          />
        )}

        {branchesFetchingStatus === 'failed' && (
          <AlertMessage>
            {typeof branchesFetchingError === 'string'
              ? branchesFetchingError
              : t('errors.somethingWentWrong', { ns: 'commons' })}
          </AlertMessage>
        )}

        {branchesFetchingStatus === 'succeeded' && repositoryId
          && (platformBranches === null || platformBranches.length === 0)
          && (
            <AlertMessage>
              {t(`${i18nBase}.errors.noBranchesFound`)}
            </AlertMessage>
          )}

        {branchesFetchingStatus === 'succeeded' && repositoryId
          && platformBranches && platformBranches.length > 0
          && selectControl}
      </div>
    )
  }
}

GitOptionImportBranch.propTypes = {
  branches: PropTypes.shape({
    gitlab: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired
    }))
  }),
  branchesFetchingError: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object
  ]),
  branchesFetchingStatus: PropTypes.string,
  dispatch: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  presentation: PropTypes.oneOf(['inline', 'stacked']),
  repositoryId: PropTypes.number,
  selectedBranch: PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired
  }),
  selectedBranchName: PropTypes.string,
  t: PropTypes.func.isRequired
}

GitOptionImportBranch.defaultProps = {
  branches: null,
  branchesFetchingError: null,
  branchesFetchingStatus: 'idle',
  presentation: 'inline',
  repositoryId: null,
  selectedBranch: null,
  selectedBranchName: null
}

const mapStateToProps = ({
  integrations: {
    branchesFetchingError,
    branchesFetchingStatus,
    branches
  }
}) => ({
  branchesFetchingError,
  branchesFetchingStatus,
  branches
})

export default withTranslation(['ProjectsPage', 'commons'])(
  connect(mapStateToProps)(GitOptionImportBranch)
)
